import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import CampaignPurchaseOrderContext from './context/CampaignPurchaseOrderContext';
import CampaignPurchaseOrderItem from './components/item/CampaignPurchaseOrderItem';

import {
  addPurchaseOrder,
  deletePurchaseOrder,
  fetchPurchaseOrder,
  flushAction,
  patchPurchaseOrder
} from './index';

import { PURCHASE_ORDER, buildPurchaseOrder } from './utils/campaign-purchase-order';

import './campaign-purchase-order.scss';

import type { State } from 'types';
import type {
  CampaignPurchaseOrder as CampaignPurchaseOrderAlias,
  CampaignPurchaseOrderContext as CampaignPurchaseOrderContextAlias,
  CampaignPurchaseOrderItemIdentifier
} from './types';

interface Props {
  campaignId: number
}

const CampaignPurchaseOrder: React.FC<Props> = ({
  campaignId
}): JSX.Element => {
  const [purchaseOrder, setPurchaseOrder] = React.useState<CampaignPurchaseOrderAlias>(PURCHASE_ORDER);
  const dispatch = useDispatch();
  const result = useSelector((state: State) => state.campaignPurchaseOrder.result);
  const organization = useSelector((state: State) => state.organizations.currentOrganization);
  const zalandoBrandsAgencyOrgId = useSelector((state: State) => (
    state.runtimeConfig.config.internalZalandoBrandsAgencyOrgId
  ));
  const isInternalZalandoBrand = organization?.agencyOrganizationId === Number(zalandoBrandsAgencyOrgId);

  const handleFetch = (): void => {
    dispatch(flushAction());
    dispatch(fetchPurchaseOrder(campaignId));
  };

  const handleDelete = (
    identifier: CampaignPurchaseOrderItemIdentifier
  ): void => {
    // if all values have been previously saved, we use patch mode. this is because we're
    // technically only deleting a single value from the db row (not the whole row)
    const isPatchMode = !isEmpty(result?.pon) && !isEmpty(result?.costCenter);

    if (isPatchMode) {
      const updatedPurchaseOrder = {
        ...purchaseOrder,
        [identifier]: {
          ...purchaseOrder[identifier],
          value: ''
        }
      };

      dispatch(patchPurchaseOrder(campaignId, updatedPurchaseOrder));
    } else {
      dispatch(deletePurchaseOrder(campaignId, purchaseOrder));
    }
  };

  const handleModalButtonClick = (
    identifier: CampaignPurchaseOrderItemIdentifier
  ): void => {
    // if no values have been previously saved, we use create mode
    const isCreateMode = isEmpty(result?.pon) && isEmpty(result?.costCenter);

    const updatedPurchaseOrder = {
      ...purchaseOrder,
      [identifier]: {
        ...purchaseOrder[identifier],
        value: purchaseOrder[identifier].value
      }
    };

    if (isCreateMode) {
      dispatch(addPurchaseOrder(campaignId, updatedPurchaseOrder));
    } else {
      dispatch(patchPurchaseOrder(campaignId, updatedPurchaseOrder));
    }

    setPurchaseOrder(updatedPurchaseOrder);
  };

  const handleCheckboxChange = (
    identifier: CampaignPurchaseOrderItemIdentifier,
    isChecked: boolean
  ): void => {
    setPurchaseOrder({
      ...purchaseOrder,
      [identifier]: {
        ...purchaseOrder[identifier],
        isChecked
      }
    });
  };

  const handleFieldChange = (
    identifier: CampaignPurchaseOrderItemIdentifier,
    value: string
  ): void => {
    setPurchaseOrder({
      ...purchaseOrder,
      [identifier]: {
        ...purchaseOrder[identifier],
        value
      }
    });
  };

  // when deleting purchase order (i.e. resetting `result` to null
  React.useEffect(() => {
    if (isNil(result)) {
      setPurchaseOrder(PURCHASE_ORDER);
      handleFetch();
    }
  }, [result]);

  // after fetch has taken place
  React.useEffect(() => {
    const updatedPurchaseOrder = buildPurchaseOrder(result);

    if (!isEqual(PURCHASE_ORDER, updatedPurchaseOrder)) {
      setPurchaseOrder(updatedPurchaseOrder);
    }
  }, [result]);

  // during initial page load
  React.useEffect(() => {
    handleFetch();

    return () => {
      dispatch(flushAction());
    };
  }, [campaignId]);

  const context = React.useMemo<CampaignPurchaseOrderContextAlias>(() => ({
    purchaseOrder,
    onDelete: handleDelete,
    onCheckboxChange: handleCheckboxChange,
    onFieldChange: handleFieldChange,
    onModalButtonClick: handleModalButtonClick
  }), [purchaseOrder]);

  return (
    <CampaignPurchaseOrderContext.Provider value={context}>
      <div className="campaign-purchase-order">
        <CampaignPurchaseOrderItem
          identifier="pon"
        />
        {
          isInternalZalandoBrand && (
            <CampaignPurchaseOrderItem
              identifier="costCenter"
            />
          )
        }
      </div>
    </CampaignPurchaseOrderContext.Provider>
  );
};

export default CampaignPurchaseOrder;
